'use client'

import type { MenuItem, MenuSeparator } from '@graftcode/design-system'
import { Avatar } from '@graftcode/design-system'

import { HEADER_TEST_IDS } from '@common/common-test-ids.const'
import { BASE_PROJECT_ITEMS, createSwitchProjectItem } from '@common/const'
import { getSafeImageUrl } from '@common/helpers'

import type { Project } from '@modules/projects/projects.type'
import type { User } from '@modules/user'

function getInitials(name?: string): string {
  if (!name) return ''

  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0)

  if (words.length >= 2) {
    const firstChar = words[0]!.charAt(0)
    const lastChar = words[words.length - 1]!.charAt(0)
    return (firstChar + lastChar).toUpperCase()
  }

  return name.substring(0, 2).toUpperCase()
}

export function createProjectItems(projects: Project[], currentProject: Project | null): (MenuItem | MenuSeparator)[] {
  const projectsForPicker =
    currentProject && !projects.some((project) => project.projectId === currentProject.projectId)
      ? [...projects, currentProject]
      : projects

  const projectGroupItems = projectsForPicker.map((project) => {
    const isSelected = currentProject?.projectId === project.projectId
    const logoUrl = getSafeImageUrl(project.logoUrl)
    const initials = getInitials(project?.name)

    return {
      type: 'item' as const,
      active: true,
      selected: isSelected,
      icon: logoUrl ? (
        <Avatar size={20} src={logoUrl} data-testid={HEADER_TEST_IDS.menuItemAvatar} />
      ) : (
        <Avatar size={20} fallback={initials} data-testid={HEADER_TEST_IDS.menuItemAvatar} />
      ),
      groups: [],
      label: project.name,
      id: `project-${project.projectId}`,
      size: 'xs' as const,
    }
  })

  return [...BASE_PROJECT_ITEMS, createSwitchProjectItem(projectGroupItems)]
}

export function createProfileItems(
  baseItems: (MenuItem | MenuSeparator)[],
  currentUser: Partial<User> | User | null
): (MenuItem | MenuSeparator)[] {
  if (!currentUser) {
    return baseItems.slice(1)
  }

  if (!baseItems.length || baseItems[0]?.type !== 'item') {
    return baseItems
  }

  const firstItem = baseItems[0] as MenuItem
  const regionalData = currentUser?.regionalData
  const email = regionalData?.userEmail || currentUser?.profile?.email || ''
  const firstName = regionalData?.firstname || ''
  const lastName = regionalData?.lastname || ''

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || firstItem.label
  const displayEmail = email || firstItem.subLabel

  const updatedFirstItem: MenuItem = {
    ...firstItem,
    label: fullName,
    subLabel: displayEmail,
  }

  return [updatedFirstItem, ...baseItems.slice(1)]
}
